/**
 * Retrieval Tester — admin surface for the KB RAG pipeline.
 *
 * Lets admins verify what the AI would retrieve for a given technician
 * context before the LLM is wired live. Mirrors the exact ``RetrievalContext``
 * shape the backend accepts:
 *
 *   scope (category / equipment / manufacturer / model)
 *   symptom
 *   raw query
 *   explicit error codes
 *   explicit components
 *   currentStep + priorAnswers (advanced)
 *
 * Renders the ranked citations with score-factor breakdowns, tier badges,
 * fabrication-guard message when triggered, and a "View Source" popover.
 * Reused by the KnowledgeBaseModal's "Retrieval Tester" tab.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Info, Loader2, Play, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { kbApi } from "../lib/api";
import { CitationCard, ViewSourceModal } from "./CitationCard";

// Same NativeSelect as the KB modal — kept inline so this file stands alone.
function NativeSelect({ value, onChange, options, placeholder, disabled, testId }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-blue-400 disabled:bg-slate-50 disabled:text-slate-400"
      data-testid={testId}
    >
      <option value="">{placeholder || "— None —"}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export default function RetrievalTesterPanel({ taxonomy }) {
  const [query, setQuery] = useState("");
  const [symptom, setSymptom] = useState("");
  const [scope, setScope] = useState({ categoryId: "", equipmentTypeId: "", manufacturerId: "", modelId: "" });
  const [errorCodesText, setErrorCodesText] = useState("");
  const [componentsText, setComponentsText] = useState("");
  const [priorAnswersText, setPriorAnswersText] = useState("");
  const [currentStep, setCurrentStep] = useState("diagnose");
  const [topK, setTopK] = useState(6);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [viewCitation, setViewCitation] = useState(null);

  // Cascading option filters
  const equipmentOptions = useMemo(
    () => taxonomy.equipmentTypes.filter((e) => !scope.categoryId || e.categoryId === scope.categoryId),
    [taxonomy.equipmentTypes, scope.categoryId],
  );
  const manufacturerOptions = useMemo(
    () => taxonomy.manufacturers.filter((m) => !scope.categoryId || m.categoryId === scope.categoryId),
    [taxonomy.manufacturers, scope.categoryId],
  );
  const modelOptions = useMemo(
    () =>
      taxonomy.models.filter(
        (m) =>
          (!scope.manufacturerId || m.manufacturerId === scope.manufacturerId) &&
          (!scope.equipmentTypeId || m.equipmentTypeId === scope.equipmentTypeId),
      ),
    [taxonomy.models, scope.manufacturerId, scope.equipmentTypeId],
  );

  const runSearch = async () => {
    if (!query.trim() && !symptom.trim() && !errorCodesText.trim() && !componentsText.trim()) {
      toast.error("Enter a query, symptom, error code, or component first");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        query: query.trim() || undefined,
        symptom: symptom.trim() || undefined,
        scope: {
          categoryId: scope.categoryId || undefined,
          equipmentTypeId: scope.equipmentTypeId || undefined,
          manufacturerId: scope.manufacturerId || undefined,
          modelId: scope.modelId || undefined,
        },
        errorCodes: errorCodesText.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean),
        components: componentsText.split(/,\s*/).map((s) => s.trim()).filter(Boolean),
        priorAnswers: priorAnswersText.split(/\n+/).map((s) => s.trim()).filter(Boolean),
        currentStep: currentStep || undefined,
        topK: Math.min(20, Math.max(1, Number(topK) || 6)),
      };
      const res = await kbApi.search(payload);
      setResult(res);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Search failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-12 gap-4 h-full">
      {/* -------- LEFT: context builder -------- */}
      <div className="col-span-5 space-y-3 overflow-y-auto pr-1">
        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
          <div>
            <Label className="text-[12px] font-medium text-slate-700">Query (technician input)</Label>
            <Textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g., Furnace fails to ignite, flame sensor voltage looks off"
              className="mt-1 min-h-[64px] text-[13px]"
              data-testid="kb-tester-query"
            />
          </div>
          <div>
            <Label className="text-[12px] font-medium text-slate-700">Symptom (session-level, optional)</Label>
            <Input
              value={symptom}
              onChange={(e) => setSymptom(e.target.value)}
              placeholder="Furnace no ignite"
              className="mt-1 h-9 text-[13px]"
              data-testid="kb-tester-symptom"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Error codes (space or comma)</Label>
              <Input
                value={errorCodesText}
                onChange={(e) => setErrorCodesText(e.target.value)}
                placeholder="E31, E42"
                className="mt-1 h-9 text-[13px]"
                data-testid="kb-tester-error-codes"
              />
            </div>
            <div>
              <Label className="text-[12px] font-medium text-slate-700">Components (comma-separated)</Label>
              <Input
                value={componentsText}
                onChange={(e) => setComponentsText(e.target.value)}
                placeholder="flame sensor, draft inducer"
                className="mt-1 h-9 text-[13px]"
                data-testid="kb-tester-components"
              />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-3">
          <div className="text-[12px] font-medium text-slate-700 flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5 text-blue-600" /> Scope (drives retrieval priority)
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] text-slate-500">Service category</Label>
              <NativeSelect
                value={scope.categoryId}
                onChange={(v) =>
                  setScope((s) => ({ categoryId: v || "", equipmentTypeId: "", manufacturerId: "", modelId: "" }))
                }
                options={taxonomy.categories.map((c) => ({ value: c.id, label: c.name }))}
                placeholder="— Any —"
                testId="kb-tester-category"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Equipment type</Label>
              <NativeSelect
                value={scope.equipmentTypeId}
                onChange={(v) => setScope((s) => ({ ...s, equipmentTypeId: v || "", modelId: "" }))}
                options={equipmentOptions.map((e) => ({ value: e.id, label: e.name }))}
                placeholder={scope.categoryId ? "— Any —" : "Pick a category first"}
                disabled={!scope.categoryId}
                testId="kb-tester-equipment"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Manufacturer</Label>
              <NativeSelect
                value={scope.manufacturerId}
                onChange={(v) => setScope((s) => ({ ...s, manufacturerId: v || "", modelId: "" }))}
                options={manufacturerOptions.map((m) => ({ value: m.id, label: m.name }))}
                placeholder={scope.categoryId ? "— Any —" : "Pick a category first"}
                disabled={!scope.categoryId}
                testId="kb-tester-manufacturer"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Model</Label>
              <NativeSelect
                value={scope.modelId}
                onChange={(v) => setScope((s) => ({ ...s, modelId: v || "" }))}
                options={modelOptions.map((m) => ({ value: m.id, label: m.name }))}
                placeholder={modelOptions.length ? "— Any —" : "Pick manufacturer + equipment first"}
                disabled={!modelOptions.length}
                testId="kb-tester-model"
              />
            </div>
          </div>
        </div>

        <details className="rounded-lg border border-slate-200 bg-white">
          <summary className="cursor-pointer px-3 py-2 text-[12px] font-medium text-slate-700">Advanced context</summary>
          <div className="px-3 py-3 border-t border-slate-100 space-y-3">
            <div>
              <Label className="text-[11px] text-slate-500">Prior technician answers (one per line)</Label>
              <Textarea
                value={priorAnswersText}
                onChange={(e) => setPriorAnswersText(e.target.value)}
                placeholder="Blue flame flickers for 3 seconds\nMeasured 0.6 microamps"
                className="mt-1 min-h-[64px] text-[13px]"
                data-testid="kb-tester-prior-answers"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-slate-500">Current diagnostic step</Label>
                <Input
                  value={currentStep}
                  onChange={(e) => setCurrentStep(e.target.value)}
                  className="mt-1 h-9 text-[13px]"
                  data-testid="kb-tester-current-step"
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Top K citations</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={topK}
                  onChange={(e) => setTopK(e.target.value)}
                  className="mt-1 h-9 text-[13px]"
                  data-testid="kb-tester-topk"
                />
              </div>
            </div>
          </div>
        </details>

        <Button
          onClick={runSearch}
          disabled={busy}
          className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white"
          data-testid="kb-tester-run"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
          Run retrieval
        </Button>
      </div>

      {/* -------- RIGHT: results -------- */}
      <div className="col-span-7 space-y-3 overflow-y-auto">
        {!result && !busy && (
          <div className="border-2 border-dashed border-slate-200 rounded-lg py-16 text-center">
            <Info className="h-6 w-6 text-slate-300 mx-auto mb-2" />
            <div className="text-[13px] text-slate-500">
              Fill in a query on the left and hit &ldquo;Run retrieval&rdquo; to preview
              what the AI would see.
            </div>
          </div>
        )}

        {busy && (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}

        {result && (
          <>
            <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 text-[11.5px] text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>
                <b>{result.diagnostics?.docsScanned || 0}</b> active docs scanned
              </span>
              <span>
                · <b>{result.diagnostics?.chunksScanned || 0}</b> chunks in pool
              </span>
              <span>
                · <b>{result.diagnostics?.chunksMatched || 0}</b> ranked
              </span>
              <span>
                · top score <b>{result.diagnostics?.topScore || 0}</b>
              </span>
              {result.context?.errorCodes?.length ? (
                <span className="ml-auto flex items-center gap-1">
                  Codes:
                  {result.context.errorCodes.map((c) => (
                    <span key={c} className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 text-[10.5px]">
                      {c}
                    </span>
                  ))}
                </span>
              ) : null}
            </div>

            {result.noResult ? (
              <div
                className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
                data-testid="kb-tester-no-result"
              >
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <div className="text-[13px] font-semibold text-amber-900 mb-1">
                    No verified documentation found
                  </div>
                  <div className="text-[12px] text-amber-800">
                    {result.noResultMessage}
                  </div>
                  <div className="text-[11px] text-amber-700 mt-1.5">
                    The AI will surface this exact message rather than fabricate an answer. Add or activate a
                    relevant document to the Knowledge Base to close this gap.
                  </div>
                </div>
              </div>
            ) : (
              (result.citations || []).map((c, i) => (
                <CitationCard
                  key={c.chunkId}
                  citation={c}
                  rank={i + 1}
                  onViewSource={setViewCitation}
                />
              ))
            )}
          </>
        )}
      </div>

      <ViewSourceModal citation={viewCitation} onClose={() => setViewCitation(null)} />
    </div>
  );
}
